import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { isPrerender } from '../utils/isPrerender';
import { matchDate, parseFrenchDate } from '../utils/utils';
import SectionTitle from './SectionTitle';

const AUTOPLAY_MS = 8000;
const SWIPE_THRESHOLD = 40;

// Date lisible ("samedi 19 septembre") à partir du dd/mm/yyyy renvoyé par la FFTT ;
// on retombe sur la chaîne brute si jamais elle ne se parse pas.
const formatMatchDate = (raw) => {
  const parsed = parseFrenchDate(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const ChevronIcon = ({ direction }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d={direction === 'prev' ? 'M14.5 5.5l-6 6.5 6 6.5' : 'M9.5 5.5l6 6.5-6 6.5'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProchainsMatch = () => {
  const {
    equipesProchainMatchs,
    prochainsMatchs,
    prochainsMatchsLoading,
    prochainsMatchsError,
    fetchProchainsMatchs,
  } = useData();

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const total = equipesProchainMatchs.length;

  useEffect(() => {
    if (isPrerender()) return;
    fetchProchainsMatchs();
  }, [fetchProchainsMatchs]);

  // Le nombre d'équipes peut changer une fois les données chargées.
  useEffect(() => {
    if (index >= total) setIndex(0);
  }, [total, index]);

  // Défilement automatique — en pause au survol/focus, jamais si l'utilisateur
  // préfère moins d'animations, jamais pendant le pré-rendu.
  useEffect(() => {
    if (isPrerender() || paused || total <= 1) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [total, paused]);

  const go = (delta) => setIndex((i) => (i + delta + total) % total);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > SWIPE_THRESHOLD) go(delta > 0 ? -1 : 1);
    touchStartX.current = null;
  };

  const equipe = equipesProchainMatchs[index];
  const match = equipe ? prochainsMatchs[equipe.idequipe] : null;

  return (
    <div className="section section--seam section--blue ccontainer-prochain-match">
      <SectionTitle overline="Compétition">Nos prochains matchs</SectionTitle>

      {prochainsMatchsLoading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement en cours...</span>
          </div>
        </div>
      ) : prochainsMatchsError ? (
        <div className="text-center">Erreur : {prochainsMatchsError}</div>
      ) : total > 0 ? (
        <div
          className="match-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div className="match-card" key={equipe.idequipe} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <h3 className="match-equipe">{equipe.libequipe}</h3>

            {match ? (
              <>
                <p className="match-date">{formatMatchDate(matchDate(match))}</p>
                <div className="match-teams">
                  <div className="match-team">
                    <span className="match-team-name">{match.equa || 'Aucune équipe'}</span>
                    {match.scorea !== '' && match.scorea != null && (
                      <span className="match-team-score">{match.scorea}</span>
                    )}
                  </div>
                  <span className="match-vs">VS</span>
                  <div className="match-team">
                    <span className="match-team-name">{match.equb || 'Aucune équipe'}</span>
                    {match.scoreb !== '' && match.scoreb != null && (
                      <span className="match-team-score">{match.scoreb}</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="match-empty">Aucun match à venir.</p>
            )}
          </div>

          {total > 1 && (
            <div className="match-nav">
              <button
                type="button"
                className="match-arrow"
                onClick={() => go(-1)}
                aria-label="Équipe précédente"
              >
                <ChevronIcon direction="prev" />
              </button>

              <div className="match-dots">
                {equipesProchainMatchs.map((e, i) => (
                  <button
                    key={e.idequipe}
                    type="button"
                    className={`match-dot${i === index ? ' is-active' : ''}`}
                    aria-label={`Voir ${e.libequipe}`}
                    aria-current={i === index}
                    onClick={() => setIndex(i)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="match-arrow"
                onClick={() => go(1)}
                aria-label="Équipe suivante"
              >
                <ChevronIcon direction="next" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="container-aucun-match">Aucun match pour le moment.</div>
      )}

      <div className="container-btn text-center">
        <Link className="btn btn-secondary" to="/matchs">Voir plus</Link>
      </div>
    </div>
  );
};

export default ProchainsMatch;
